import {Controller} from "@hotwired/stimulus";
import {patch} from "@rails/request.js";

export default class extends Controller {
  static targets = ["titleInput", "summaryInput", "summaryCount", 'aboutInput', 'startDate', 'endDate', 'startTime', 'endTime',
    'refineInstructions', 'aiMenu', 'aiRefineSection', 'loadingOverlay', 'loadingText'];

  static values = {
    generatingLabel: { type: String, default: "Generating..." },
    refiningLabel: { type: String, default: "Refining..." },
    placeholderGenerating: { type: String, default: "Generating description..." },
    placeholderRefining: { type: String, default: "Refining description..." }
  };

  generateAISummary(event) {
    const title = this.titleInputTarget;
    if (title.value.trim() === '') {
      title.focus();
      return;
    }
    this.sendRequest(event.currentTarget, title.value, {});
  }

  updateSummaryCount() {
    if (!this.hasSummaryCountTarget || !this.hasSummaryInputTarget) return;
    const input = this.summaryInputTarget;
    const max = input.maxLength > 0 ? input.maxLength : 150;
    this.summaryCountTarget.textContent = `${input.value.length} / ${max}`;
  }

  runAiAction(event) {
    const button = event.currentTarget;
    const actionKind = button.dataset.refineAction;
    const title = this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : '';

    if (title === '' && this.hasTitleInputTarget) {
      this.titleInputTarget.focus();
      return;
    }

    if (actionKind === 'generate') {
      this.closeAiMenu();
      this.sendRequest(button, title, { mode: 'generate' });
      return;
    }

    let instructions = '';
    if (actionKind === 'custom') {
      if (!this.hasRefineInstructionsTarget) return;
      instructions = this.refineInstructionsTarget.value.trim();
      if (instructions === '') {
        this.refineInstructionsTarget.focus();
        return;
      }
    }

    if (!this.currentAboutHTML()) return;
    this.closeAiMenu();
    this.sendRequest(button, title, { actionKind, instructions, mode: 'refine' });
  }

  runAiActionFromInput(event) {
    event.preventDefault();
    const applyBtn = this.element.querySelector('.ai-menu-apply');
    if (applyBtn) applyBtn.click();
  }

  syncRefineVisibility() {
    if (!this.hasAiRefineSectionTarget) return;
    const hasContent = this.currentAboutHTML() !== '';
    this.aiRefineSectionTarget.classList.toggle('d-none', !hasContent);
  }

  closeAiMenu() {
    const wrapper = this.element.querySelector('[data-controller~="ai-menu"]');
    if (!wrapper) return;
    const menu = wrapper.querySelector('[data-ai-menu-target="menu"]');
    if (menu) menu.classList.add('d-none');
    wrapper.classList.remove('is-open');
    const toggleBtn = wrapper.querySelector('.ai-menu-toggle');
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
  }

  async sendRequest(currentTarget, title, { actionKind = '', instructions = '', mode = '' } = {}) {
    const generateSummary = currentTarget.dataset.summary === 'true';
    const isRefine = mode === 'refine';
    const isAboutRequest = !generateSummary;

    this.addLoader(currentTarget, { isRefine: isAboutRequest && isRefine });
    if (isAboutRequest) this._showAboutLoading(isRefine);

    const url = '/organizer/events/generate_ai_summary';
    const date_time_obj = generateSummary ? '' : this.setDateTime();

    const response = await patch(url, {
      contentType: "application/json",
      body: {
        event_title: title,
        generate_summary: generateSummary,
        event_summary: this.hasSummaryInputTarget ? this.summaryInputTarget.value : '',
        date_time_obj: date_time_obj,
        current_about: generateSummary ? '' : this.currentAboutHTML(),
        location: this.readFormField('event[location]'),
        action_kind: actionKind,
        instructions: instructions
      },
      responseKind: "json",
    });

    if (response.ok) {
      const data = await response.json;

      if (generateSummary) {
        this.summaryInputTarget.value = data.response;
        this.updateSummaryCount();
      } else {
        this.writeAbout(data.response);
        if (this.hasRefineInstructionsTarget) this.refineInstructionsTarget.value = '';
      }
    }
    this.removeLoader(currentTarget);
    if (isAboutRequest) this._hideAboutLoading();
  }

  _showAboutLoading(isRefine) {
    if (this.hasLoadingTextTarget) {
      this.loadingTextTarget.textContent = isRefine
        ? this.placeholderRefiningValue
        : this.placeholderGeneratingValue;
    }
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('d-none');
    }
    const toggle = this._aiToggleButton();
    if (toggle) {
      const labelText = toggle.querySelector('.ai-menu-label-text');
      if (labelText) {
        labelText.dataset.originalText = labelText.textContent;
        labelText.textContent = isRefine ? this.refiningLabelValue : this.generatingLabelValue;
      }
      toggle.classList.add('is-busy');
    }
  }

  _hideAboutLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add('d-none');
    }
    const toggle = this._aiToggleButton();
    if (toggle) {
      const labelText = toggle.querySelector('.ai-menu-label-text');
      if (labelText && labelText.dataset.originalText) {
        labelText.textContent = labelText.dataset.originalText;
        delete labelText.dataset.originalText;
      }
      toggle.classList.remove('is-busy');
    }
  }

  currentAboutHTML() {
    if (!this.hasAboutInputTarget) return '';
    const el = this.aboutInputTarget;
    const html = (el.value || '').trim();
    if (!html) return '';
    // Trix renders an empty editor as "<div><br></div>", treat as empty.
    const textOnly = html.replace(/<[^>]*>/g, '').trim();
    return textOnly === '' ? '' : html;
  }

  writeAbout(html) {
    if (!this.hasAboutInputTarget) return;
    const el = this.aboutInputTarget;
    if (el.editor && typeof el.editor.loadHTML === 'function') {
      el.editor.loadHTML(html);
    } else {
      el.value = html;
    }
  }

  readFormField(name) {
    const el = this.element.querySelector(`[name="${name}"]`);
    return el ? (el.value || '') : '';
  }

  addLoader(currentTarget) {
    this._toggleLoader(currentTarget, true, { keepLabel: false });
    const toggleBtn = this._aiToggleButton();
    if (toggleBtn && toggleBtn !== currentTarget) {
      this._toggleLoader(toggleBtn, true, { keepLabel: true });
    }
  }

  removeLoader(currentTarget) {
    this._toggleLoader(currentTarget, false, { keepLabel: false });
    const toggleBtn = this._aiToggleButton();
    if (toggleBtn && toggleBtn !== currentTarget) {
      this._toggleLoader(toggleBtn, false, { keepLabel: true });
    }
  }

  _toggleLoader(btn, loading, { keepLabel = false } = {}) {
    if (!btn) return;
    const spinner = btn.firstElementChild;
    const label = spinner && spinner.nextElementSibling;
    if (!spinner || !label) return;
    spinner.classList.toggle('d-none', !loading);
    if (!keepLabel) {
      label.classList.toggle('d-none', loading);
    }
    btn.disabled = loading;
  }

  _aiToggleButton() {
    return this.element.querySelector('.ai-menu-toggle');
  }

  setDateTime() {
    return {
      start_date: this.setDateTimeValue(this.startDateTargets), end_date: this.setDateTimeValue(this.endDateTargets),
      start_time: this.setDateTimeValue(this.startTimeTargets), end_time: this.setDateTimeValue(this.endTimeTargets)
    }
  }

  setDateTimeValue(targets) {
    for (const target of targets) {
      if (target.value !== '') return target.value;
    }
    return ''
  }
}
